import { createContext, useContext, useRef, useState } from "react";

import { Sidebar } from "@/components/sidebar";
import { useFocusRestoration } from "@/utils/useFocusRestoration";
import { useRovingNav } from "@/utils/useRovingNav";

import { useRowFocusWithin } from "./useRowFocusWithin";

import type { ComponentProps, ElementType, FocusEvent, KeyboardEvent, RefObject } from "react";

const TREE_ITEM = '[role="treeitem"]';

const TreeItemFocusContext = createContext<{ focused: boolean; focusVisible: boolean } | undefined>(
  undefined,
);

/**
 * Keyboard navigation for a sidebar tree, following the APG treeview: Up/Down/Home/End move between
 * the visible treeitems. The Tab stop is the current page's item, else the last focused one, else the
 * first. `tabStop` skips the parents, which contain the page's item too.
 */
export function useSidebarNav(treeRef: RefObject<HTMLElement | null>) {
  useRovingNav(treeRef, "vertical", {
    itemSelector: TREE_ITEM,
    tabStop: (item) =>
      !!item.querySelector('[aria-current="page"]') && !item.querySelector(TREE_ITEM),
  });
  useFocusRestoration(treeRef, TREE_ITEM);
}

/**
 * Keys for a treeitem acting on itself, not on its inner controls: Enter and Space toggle a parent or
 * activate a leaf; Right opens a closed parent or else enters its first child; Left closes an open
 * parent or else moves to the enclosing item.
 */
export function useTreeItemKeys({
  isParent = false,
  isOpen = false,
  onToggle,
  onActivate,
}: {
  isParent?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onActivate?: () => void;
}) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    const item = event.currentTarget;

    const run = (action: () => void) => {
      event.preventDefault();
      action();
    };
    switch (event.key) {
      case "Enter":
      case " ":
        if (isParent) return run(() => onToggle?.());
        // A leaf without an action keeps its button's native click
        if (onActivate) return run(onActivate);
        return;
      case "ArrowRight":
        return run(() => {
          if (isParent && !isOpen) onToggle?.();
          else item.querySelector<HTMLElement>(TREE_ITEM)?.focus();
        });
      case "ArrowLeft":
        return run(() => {
          if (isParent && isOpen) onToggle?.();
          else item.parentElement?.closest<HTMLElement>(TREE_ITEM)?.focus();
        });
    }
  };
}

/**
 * A treeitem of the sidebar tree. `focused` says whether focus is inside it, which sets the Tab stops
 * of its controls (see useTreeItemControlTabIndex). The caller owns the focus tracking.
 */
export function TreeItem({
  as: Component = "div",
  focused,
  isParent,
  isOpen,
  onToggle,
  onActivate,
  onFocus,
  onBlur,
  ...props
}: ComponentProps<"div"> & {
  as?: ElementType;
  focused: boolean;
  isParent?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onActivate?: () => void;
}) {
  const [focusVisible, setFocusVisible] = useState(false);
  const onKeyDown = useTreeItemKeys({ isParent, isOpen, onToggle, onActivate });

  // Focus inside a child item doesn't make this one focus-visible
  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setFocusVisible(event.currentTarget.matches(":focus-visible"));
    }
    onFocus?.(event);
  };
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) setFocusVisible(false);
    onBlur?.(event);
  };

  return (
    <TreeItemFocusContext value={{ focused, focusVisible }}>
      <Component
        role="treeitem"
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
    </TreeItemFocusContext>
  );
}

/**
 * Tab stop for a control inside a treeitem: out of the Tab order unless the treeitem holds focus, so
 * Tab goes from the focused item to its controls and then leaves the tree. Outside a treeitem, the
 * control stays a normal Tab stop.
 */
export function useTreeItemControlTabIndex() {
  return useContext(TreeItemFocusContext)?.focused === false ? -1 : undefined;
}

/** Whether the treeitem around this control has keyboard focus, so the control should show */
export function useTreeItemFocusVisible() {
  return useContext(TreeItemFocusContext)?.focusVisible ?? false;
}

/**
 * The sidebar's tree root: its treeitems, the sections and rows inside it, are navigated together. The
 * label names the tree itself; the landmark around it doesn't name the tree.
 */
export function NavTree(props: ComponentProps<"div"> & { "aria-label": string }) {
  const treeRef = useRef<HTMLDivElement>(null);
  useSidebarNav(treeRef);

  return <div ref={treeRef} role="tree" {...props} />;
}

/**
 * A group of sidebar rows. Given `expanded`, the section is a treeitem of its NavTree: its header
 * toggles it and its contents are its group, so the header goes first and the rows inside a `ul`
 * with `role="group"`.
 */
export function NavSection({
  expanded,
  onToggle,
  ...props
}: ComponentProps<typeof Sidebar.GroupSection> & {
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const { focusWithin, focusWithinProps } = useRowFocusWithin();

  if (expanded === undefined) return <Sidebar.GroupSection {...props} />;

  return (
    <TreeItem
      as={Sidebar.GroupSection}
      focused={focusWithin}
      {...focusWithinProps}
      isParent
      isOpen={expanded}
      onToggle={onToggle}
      aria-expanded={expanded}
      {...props}
    />
  );
}
